import { LegalEntityMatchResult } from '../../../models/bill-import.models';
import {
  FieldError,
  StepValidationResult,
  ValidationRule,
  getByPath,
  isEmpty,
} from '../validation';

/** Billing address shape used by the LEN step (component-level state) */
export interface LenAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}

const ONLY_IF_NEW = (ctx: { isNew: boolean }) => ctx.isNew;

export const LEN_RULES: ValidationRule[] = [
  { field: 'name',                    label: 'Legal Entity Name', severity: 'required', condition: ONLY_IF_NEW },
  { field: 'billingAddress.street1',  label: 'Billing Street 1',  severity: 'required', condition: ONLY_IF_NEW },
  { field: 'billingAddress.city',     label: 'Billing City',      severity: 'required', condition: ONLY_IF_NEW },
  { field: 'billingAddress.state',    label: 'Billing State',     severity: 'required', condition: ONLY_IF_NEW },
  { field: 'billingAddress.zip',      label: 'Billing ZIP',       severity: 'required', condition: ONLY_IF_NEW },
];

export interface LenValidationInput {
  legalEntities: LegalEntityMatchResult[];
  entityNameOverrides: Record<string, string>;
  /** Extracted LEN names from the match request, keyed by groupId. Used as fallback when no override. */
  extractedLenNames: Record<string, string>;
  lenAddressDataMap: Record<string, LenAddress>;
  isNewEntity: (groupId: string) => boolean;
  /**
   * Map of groupId → selected existing-LEN id (UUID string) or null/undefined for new.
   * Used to detect two groupIds resolving to the same existing LEN (user match mistake).
   */
  selections: Record<string, number | string | null>;
}

/**
 * Build the synthetic object the rules walk via getByPath. The LEN step's
 * name and billing address live in different state stores:
 *   - name: store.entityNameOverrides (user edit) with extractedLenNames
 *     (matchRequest) as fallback — covers the common case where the user
 *     accepts the AI-extracted name without typing anything.
 *   - billingAddress: component-level lenAddressDataMap (also mirrored to
 *     store.matchRequest via updateLenBillingAddress so buildImportRequest
 *     sees the live value).
 *
 * projectLen stitches them into a single object so rules defined as
 * 'billingAddress.zip' etc. can resolve paths normally. Do NOT try to
 * simplify this by passing the raw LegalEntityMatchResult — that would
 * silently drop the user's edits.
 */
interface LenEditProjection {
  name: string;
  billingAddress: Partial<LenAddress>;
}

function projectLen(
  groupId: string,
  entityNameOverrides: Record<string, string>,
  extractedLenNames: Record<string, string>,
  lenAddressDataMap: Record<string, LenAddress>,
): LenEditProjection {
  return {
    name: entityNameOverrides[groupId] ?? extractedLenNames[groupId] ?? '',
    billingAddress: lenAddressDataMap[groupId] ?? {},
  };
}

function describeLen(groupId: string, projection: LenEditProjection): string {
  return projection.name?.trim() || `Legal Entity ${groupId.slice(0, 8)}`;
}

export function validateLenStep(input: LenValidationInput): StepValidationResult {
  const errors: FieldError[] = [];
  const warnings: FieldError[] = [];

  // Track selected existing-LEN ids to detect duplicates (two groupIds
  // matched to the same existing record). See also the corresponding check
  // in BillAiFlowStore.buildImportRequest — this one catches the issue at
  // the LEN step so users get inline feedback instead of hitting it at import.
  const selectionToGroupId = new Map<string, string>();

  for (const len of input.legalEntities) {
    const projection = projectLen(len.groupId, input.entityNameOverrides, input.extractedLenNames, input.lenAddressDataMap);
    const entityLabel = describeLen(len.groupId, projection);
    const isNew = input.isNewEntity(len.groupId);

    for (const rule of LEN_RULES) {
      if (rule.condition && !rule.condition({ entity: projection, isNew })) continue;
      const value = getByPath(projection, rule.field);
      if (isEmpty(value)) {
        const err: FieldError = {
          entityId: len.groupId,
          entityLabel,
          field: rule.field,
          label: rule.label,
          severity: rule.severity,
        };
        if (rule.severity === 'required') errors.push(err);
        else warnings.push(err);
      }
    }

    // Duplicate-selection check: if this LEN is linked to an existing ID
    // that another LEN is also linked to, flag both.
    const selection = input.selections[len.groupId];
    if (selection != null && selection !== 'new_record') {
      const key = String(selection);
      const firstGroupId = selectionToGroupId.get(key);
      if (firstGroupId && firstGroupId !== len.groupId) {
        errors.push({
          entityId: len.groupId,
          entityLabel,
          field: 'match',
          label: 'Matched to the same existing record as another legal entity',
          severity: 'required',
        });
      } else {
        selectionToGroupId.set(key, len.groupId);
      }
    }
  }

  return { errors, warnings, hasBlockers: errors.length > 0 };
}
